import React from 'react';
import { ClickAction, Slot } from './calendar';
import LightRope from './LightRope';
import { setVariant, triggerEffect, useSeasonState } from './store';
import { useMediaQuery } from './useMediaQuery';
import { useSeason } from './useSeason';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

interface SeasonalDecorProps {
  slot: Slot;
  /** En la barra lateral colapsada no hay sitio: se oculta. */
  hidden?: boolean;
}

const act = (action: ClickAction) => {
  if ('variant' in action) setVariant(action.variant);
  else triggerEffect(action.effect);
};

/**
 * Decoración de temporada para una ranura de la interfaz. Sin temporada activa, o sin
 * imágenes para esa ranura en la variante actual, no pinta nada: la app se ve como
 * siempre.
 *
 * Con `prefers-reduced-motion: reduce` cada imagen pinta su `still` en vez de `src`
 * (el mismo número de imágenes: todo GIF declara uno) y la banda de luces no se monta
 * (E07-F01, R10).
 */
const SeasonalDecor: React.FC<SeasonalDecorProps> = ({ slot, hidden = false }) => {
  const season = useSeason();
  const { variant } = useSeasonState();
  const reducedMotion = useMediaQuery(REDUCED_MOTION);
  if (hidden || !season) return null;
  const assets = (season.variants[variant] ?? season.variants.default)[slot];
  if (assets.length === 0) return null;

  return (
    <div
      className={`seasonal-decor seasonal-decor--${slot} seasonal-decor--${season.id}`}
      aria-label={season.label}
      data-season={season.id}
      data-variant={variant}
    >
      <div className="seasonal-decor__row">
      {assets.map((a, i) => {
        const clickable = Boolean(a.onClick);
        return (
          <img
            // La posición forma parte de la clave: en la variante `pan` la misma
            // imagen aparece dos veces.
            key={`${i}-${a.src}`}
            src={reducedMotion ? a.still ?? a.src : a.src}
            alt={a.alt}
            height={a.height}
            style={{ height: a.height }}
            loading="lazy"
            draggable={false}
            className={clickable ? 'seasonal-decor__toggle' : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => act(a.onClick!) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      act(a.onClick!);
                    }
                  }
                : undefined
            }
          />
        );
      })}
      </div>
      {/* Banda bajo la cintilla, del mismo ancho: se recorta y escala con ella. */}
      {slot === 'header' && season.headerBand === 'lightrope' && !reducedMotion && <LightRope />}
    </div>
  );
};

export default SeasonalDecor;
